import React, { ReactNode, useMemo, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import moment from "moment";
import {
  BacktestingJobResults,
  BacktestingRunType,
  BotData,
  BotGraphType,
  FormattedChartDataPoint,
  LanguageNameSpaces,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TIME_RANGE_START } from "@shared/common/constants";
import { useBotChart } from "@shared/features/bots/hooks";
import { BotGraphTypeToDisplay } from "@shared/features/bots/constants";
import useBacktestingChart from "@shared/features/bots/BotForm/Backtesting/useBacktestingChart";
import useStatistics from "@shared/features/bots/BotForm/Backtesting/useStatistics";
import { BacktestingGeneralStatistics } from "@shared/features/bots/BotForm/common/types";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import Panel from "common/NewLook/Panel.tsx";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import useMeasure from "utils/hooks/useMeasure";
import NewLookProfitChart, {
  ProfitPoint,
} from "features/bots/NewLook/NewLookProfitChart";
import { formatSignedPercent } from "features/bots/BotForm/Backtesting/NewLook/format";
import { splitFraction } from "features/portfolio/NewLook/format.ts";
import useBacktestMoney from "features/bots/BotForm/Backtesting/NewLook/useBacktestMoney";
import { formatDateLabel } from "@shared/utils/date";

type ChartKind = "accumulated" | "daily";
const CHART_KINDS: readonly ChartKind[] = ["accumulated", "daily"];
/** Below this width the side figures move under the chart. */
const STACK_WIDTH = 640;

interface BacktestPerformanceProps {
  backtestingRun?: BacktestingRunType;
  botData?: BotData;
  liveBarChartData?: FormattedChartDataPoint[];
  backtestingJobResults?: BacktestingJobResults;
  /** The run (or its figures) is still loading. */
  loading?: boolean;
  /** Offers the profit-by-day bars next to the accumulated line. */
  showDaily?: boolean;
  /** A smaller headline, where the backtest isn't the page's lead. */
  compact?: boolean;
  /**
   * Inside another panel (the bot form's preview): no panel of its own; the
   * split sits under the chart in an inset well.
   */
  bare?: boolean;
  chartHeight?: number;
  /** Shown above the headline (e.g. a title row). */
  header?: ReactNode;
  /** Right of the headline when there's no chart switch (e.g. the period). */
  aside?: ReactNode;
}

const toPoints = (data?: FormattedChartDataPoint[]) =>
  (data || [])
    .filter((point) => point && point.rawX !== undefined)
    .map((point) => ({ time: Number(point.rawX), value: Number(point.y) }));

/**
 * New look: a backtest's result as a panel led by its net PnL, built like
 * the dashboard's overview and the bots' performance panel — the headline
 * over the accumulated profit (or the profit by day), the headline and the
 * figures beside it following the crosshair, and the realized / unrealized
 * split and the initial funds in a side column.
 */
const BacktestPerformance = ({
  backtestingRun,
  botData,
  liveBarChartData,
  backtestingJobResults,
  loading,
  showDaily,
  compact,
  bare,
  chartHeight = 200,
  header,
  aside,
}: BacktestPerformanceProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const { symbol, money, signedMoney, compactMoney } = useBacktestMoney();
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const stacked = bare || (!!width && width < STACK_WIDTH);
  const [chartKind, setChartKind] = useState<ChartKind>("accumulated");
  const [hovered, setHovered] = useState<{
    kind: ChartKind;
    index: number;
  }>();

  const { titleToValue, getChangeValue, getHelperProps } = useStatistics({
    backtestingRun,
  });
  const initialFunds = Number(
    titleToValue[BacktestingGeneralStatistics.INITIAL_FUNDS]
  );
  const netPnl = Number(titleToValue[BacktestingGeneralStatistics.NET_PNL]);
  const realizedPnl = Number(
    titleToValue[BacktestingGeneralStatistics.REALIZED_PNL]
  );
  const unrealizedPnl = Number(
    titleToValue[BacktestingGeneralStatistics.UNREALIZED_PNL]
  );

  // The accumulated profit: net (the line) and realized (read out beside).
  const { lineChartMultiData, isFetching: lineFetching } = useBacktestingChart({
    backtestingRun,
  });
  const realizedData = lineChartMultiData[0]?.data;
  const netData = lineChartMultiData[1]?.data;
  const netPoints = useMemo(() => toPoints(netData), [netData]);
  const realizedPoints = useMemo(() => toPoints(realizedData), [realizedData]);

  // The profit by day, from the backtest's bot (live while it runs).
  const { graphData: dailyData, isFetching: dailyFetching } = useBotChart({
    botId: showDaily ? backtestingRun?.bot_id : undefined,
    chartType: BotGraphType.PROFIT_BY_DAY,
    botData,
    isBacktesting: true,
    defaultTimeRange: TIME_RANGE_START,
    liveChartData: backtestingJobResults
      ? {
          data: liveBarChartData,
          timeframe: backtestingRun?.backtesting_time_range,
          currentJobTimestamp: backtestingJobResults?.current_timestamp,
        }
      : undefined,
  });
  const dailyPoints = useMemo(
    () => toPoints(dailyData as FormattedChartDataPoint[] | undefined),
    [dailyData]
  );

  const kind = showDaily ? chartKind : "accumulated";
  const hoveredNet =
    hovered?.kind === "accumulated" ? netPoints[hovered.index] : undefined;
  const hoveredRealized =
    hovered?.kind === "accumulated" ? realizedPoints[hovered.index] : undefined;
  const hoveredDay =
    hovered?.kind === "daily" ? dailyPoints[hovered.index] : undefined;

  const headline = hoveredNet ? hoveredNet.value : netPnl;
  const percentOf = (value: number) =>
    initialFunds ? (value / initialFunds) * 100 : 0;
  const formatDate = (time: number) =>
    formatDateLabel(moment.unix(time), "MMM D, YYYY");

  let changeValue: string = formatSignedPercent(
    Number(getChangeValue(BacktestingGeneralStatistics.NET_PNL)) || 0
  );
  let changeColor = directionColor(tokens, headline);
  let changeMeta = t("backtesting.statistics.return_on_funds", {
    amount: money(initialFunds),
  });
  if (hoveredNet) {
    changeValue = formatSignedPercent(percentOf(hoveredNet.value));
    changeMeta = t("performance.on_day", { time: formatDate(hoveredNet.time) });
  } else if (hoveredDay) {
    changeValue = signedMoney(hoveredDay.value);
    changeColor = directionColor(tokens, hoveredDay.value);
    changeMeta = t("performance.on_day", { time: formatDate(hoveredDay.time) });
  }

  const realized = hoveredRealized ? hoveredRealized.value : realizedPnl;
  const unrealized =
    hoveredNet && hoveredRealized
      ? hoveredNet.value - hoveredRealized.value
      : unrealizedPnl;

  const chartLabel = (chart: ChartKind) =>
    t(
      BotGraphTypeToDisplay[
        chart === "accumulated"
          ? BotGraphType.ACCUMULATING_PROFIT
          : BotGraphType.PROFIT_BY_DAY
      ]
    );

  const renderChart = () => {
    const points = kind === "accumulated" ? netPoints : dailyPoints;
    const fetching = kind === "accumulated" ? lineFetching : dailyFetching;
    if ((loading || fetching) && points.length < 2)
      return <Skeleton width="100%" height={chartHeight} radius={8} />;
    if (points.length < 2)
      return (
        <NoHistory style={{ height: chartHeight }}>
          {t("empty_states.no_data")}
        </NoHistory>
      );
    return (
      <NewLookProfitChart
        points={points as ProfitPoint[]}
        height={chartHeight}
        mode={kind === "accumulated" ? "line" : "bars"}
        intraday={false}
        formatAxisValue={compactMoney}
        ariaLabel={chartLabel(kind)}
        data-testid="backtest-performance-chart"
        onActiveChange={(point) =>
          setHovered((current) => {
            if (point) return { kind, index: points.indexOf(point) };
            return current?.kind === kind ? undefined : current;
          })
        }
      />
    );
  };

  const [whole, fraction] = splitFraction(
    money(Math.abs(headline)).replace(symbol, "")
  );

  const metric = (
    key: BacktestingGeneralStatistics,
    value: number,
    signed: boolean,
    meta?: ReactNode
  ) => (
    <Metric key={key}>
      <Label>
        {t(key)}
        <HelperTooltip {...getHelperProps(key)} />
      </Label>
      {loading ? (
        <Skeleton width={96} height={26} />
      ) : (
        <MetricValue
          style={signed ? { color: directionColor(tokens, value) } : undefined}
        >
          {signed ? signedMoney(value) : money(value)}
        </MetricValue>
      )}
      {meta && !loading && <Meta>{meta}</Meta>}
    </Metric>
  );

  const content = (
    <>
      {header}
      <Layout ref={measureRef} $stacked={stacked}>
        <Main $compact={compact} $bare={bare}>
          <MainHeader>
            <div>
              <Label>
                {t(BacktestingGeneralStatistics.NET_PNL)}
                <HelperTooltip
                  {...getHelperProps(BacktestingGeneralStatistics.NET_PNL)}
                />
              </Label>
              {loading ? (
                <Skeleton
                  width={compact ? 140 : 200}
                  height={compact ? 26 : 40}
                  style={{ marginTop: 4 }}
                />
              ) : (
                <Headline
                  $compact={compact}
                  style={{ color: directionColor(tokens, headline) }}
                  aria-live="polite"
                >
                  {headline > 0 ? "+" : headline < 0 ? "−" : ""}
                  <Currency $compact={compact}>{symbol}</Currency>
                  {whole}
                  {fraction}
                </Headline>
              )}
              {!loading && (
                <ChangeLine>
                  <ChangeValue style={{ color: changeColor }}>
                    {changeValue}
                  </ChangeValue>
                  <Meta>{changeMeta}</Meta>
                </ChangeLine>
              )}
            </div>
            {!showDaily && aside}
            {showDaily && (
              <SegmentTabs<ChartKind>
                options={CHART_KINDS}
                value={chartKind}
                onChange={(next) => {
                  setHovered(undefined);
                  setChartKind(next);
                }}
                ariaLabel={t("performance.chart_type_label")}
                getLabel={chartLabel}
              />
            )}
          </MainHeader>
          <ChartArea>{renderChart()}</ChartArea>
        </Main>
        <Side $stacked={stacked} $bare={bare}>
          {metric(
            BacktestingGeneralStatistics.REALIZED_PNL,
            realized,
            true,
            formatSignedPercent(percentOf(realized))
          )}
          {metric(
            BacktestingGeneralStatistics.UNREALIZED_PNL,
            unrealized,
            true,
            formatSignedPercent(percentOf(unrealized))
          )}
          {metric(
            BacktestingGeneralStatistics.INITIAL_FUNDS,
            initialFunds,
            false,
            backtestingRun?.number_of_trades !== undefined
              ? t("backtesting.statistics.num_trades", {
                  num: backtestingRun.number_of_trades,
                })
              : undefined
          )}
        </Side>
      </Layout>
    </>
  );

  if (bare) return <div data-testid="backtest-performance">{content}</div>;
  return (
    <Panel flush={true} data-testid="backtest-performance">
      {content}
    </Panel>
  );
};

export default React.memo(BacktestPerformance);

const Layout = styled.div<{ $stacked: boolean }>`
  display: grid;
  grid-template-columns: ${(p) =>
    p.$stacked ? "minmax(0, 1fr)" : "minmax(0, 1fr) 220px"};
`;
const Main = styled.div<{ $compact?: boolean; $bare?: boolean }>`
  min-width: 0;
  padding: ${(p) =>
    p.$bare ? "0" : p.$compact ? "16px 16px 12px 20px" : "20px 20px 16px"};
`;
const MainHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
`;
const Label = styled.div`
  display: flex;
  align-items: center;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
`;
const Headline = styled.div<{ $compact?: boolean }>`
  margin-top: 4px;
  ${(p) =>
    p.$compact
      ? css`
          ${newLookType.figureLarge};
          margin-top: 2px;
        `
      : newLookType.display};
  white-space: nowrap;
`;
const Currency = styled.span<{ $compact?: boolean }>`
  margin-right: 1px;
  font-size: ${(p) => (p.$compact ? 17 : 26)}px;
  font-weight: 500;
`;
const ChangeLine = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
`;
const ChangeValue = styled.span`
  ${newLookType.meta};
  font-size: 13px;
  font-weight: 500;
`;
const Meta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const ChartArea = styled.div`
  margin-top: 16px;
`;
const NoHistory = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  text-align: center;
`;
const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 14px 0;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  &:last-child {
    padding-bottom: 0;
  }
`;
const MetricValue = styled.div`
  ${newLookType.figureLarge};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const Side = styled.div<{ $stacked: boolean; $bare?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 6px 20px 20px;
  border-left: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  background: ${(p) => getNewLookTokens(p.theme).well};
  border-radius: 0 ${NEW_LOOK_RADIUS.panel}px ${NEW_LOOK_RADIUS.panel}px 0;
  ${(p) =>
    p.$stacked &&
    css`
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      column-gap: 16px;
      padding: 4px 16px 16px;
      border-left: none;
      border-top: 1px solid ${getNewLookTokens(p.theme).hairline};
      border-radius: 0 0 ${NEW_LOOK_RADIUS.panel}px ${NEW_LOOK_RADIUS.panel}px;
      && > ${Metric} {
        padding: 12px 0 0;
        border-top: none;
      }
      ${Label} {
        white-space: normal;
      }
      ${MetricValue} {
        font-size: 17px;
        line-height: 22px;
      }
    `}
  ${(p) =>
    p.$bare &&
    css`
      margin-top: 16px;
      padding: 4px 16px 12px;
      border: 1px solid ${getNewLookTokens(p.theme).hairline};
      border-radius: ${NEW_LOOK_RADIUS.control}px;
    `}
`;
